import { filterValues, operatorValues } from "../constants";
import { filterType, FilterValue } from "schemas/elements";
import { TextValidation } from "validation/validation";
import {
	Clause,
	Operators,
	QueryClause,
	QueryStatement,
	Statements,
} from "lib/api/types";

const operands = ["AND", "OR", "NOT"];

/**
 * Check how deep is the depth of filter
 *
 * @param data filter to be checked on how deep the depth is
 * @param depth depth of the filter
 * @returns number
 */

const checkFilterDepth = (data: Clause | QueryClause, depth = 0): number => {
	if (Array.isArray(data))
		data.forEach(v => {
			const keys = Object.keys(v).filter((k: string) =>
				operands.includes(k)
			);
			depth = keys.reduce((prev, curr) => {
				const clause = v[curr as Operators] as Clause;
				const d = checkFilterDepth(clause, depth);
				return d > prev ? d : prev;
			}, 0);
		});
	else {
		const filter = data[filterValues.AND];
		if (filter !== undefined) depth = checkFilterDepth(filter);
		else depth = 5;
	}
	return ++depth;
};

/**
 * Checks whether the filter is valid in form service format
 *
 * @param data filter to be validated
 * @returns boolean
 */
export const isFilterValid = (data: Clause | QueryClause): boolean => {
	return !(checkFilterDepth(data) > 4);
};

/**
 * Formats the form service filter into something that is more manageable
 *
 * @param data filter in form service format
 * @param output current formatted filter value
 * @param filter filter type of array passed
 * @param parentFilter filter type of the parent of the array passed
 * @returns filter into formatted array
 */
export const formatFilter = (
	data: Clause | QueryClause | QueryStatement,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	output: any = [],
	filter?: filterType,
	parentFilter?: filterType
) => {
	if (!output) return false;
	if (Array.isArray(data)) {
		// If the filter is OR then we need to handle the values differently
		// so that it will belong to the same parent
		if (filter === filterValues.OR) {
			let valid = true;
			Object.values(data).forEach(key => {
				if (!valid) return;
				const keyVal = Object.keys(key)[0];
				if (
					keyVal === filterValues.AND ||
					keyVal === filterValues.OR ||
					keyVal === filterValues.NOT
				)
					valid = false;
			});
			if (!valid) {
				output = false;
			} else {
				output = [
					...output,
					{
						filter,
						parentFilter,
						field: Object.keys(data[0])[0],
						operator: operatorValues.ONE_OF,
						// Items belonging to the same OR filter will be grouped together
						value: Object.values(data)
							.map(item => formatFilter(item))
							.flat(1),
					},
				];
			}
		} else {
			data.forEach(
				item =>
					(output = formatFilter(item, output, filter, parentFilter))
			);
		}
	} else {
		Object.keys(data).forEach(key => {
			if (Array.isArray(data[key as Operators])) {
				const clause = data[key as Operators] as Clause;
				if (clause) {
					output = formatFilter(
						clause,
						output,
						key as filterType,
						filter
					);
				}
			} else {
				const statement = (data as QueryStatement)[key];
				if ("range" in statement) {
					const format = statement.range?.format;
					// If a format is entered, remove format out of values and insert it individually into each range filter
					const rangeItems = Object.entries(statement.range)
						.filter(item => {
							return item[0] !== "format";
						})
						.map(item => {
							return {
								filter,
								parentFilter,
								field: key,
								operator: item[0],
								value: {
									value: item[1],
									...(format && { format }),
								},
							};
						});
					output = [...output, ...rangeItems];
				} else {
					const operator = Object.keys(
						statement
					)[0] as keyof Statements;
					const ob = {
						filter,
						parentFilter,
						field: key,
						operator: operator,
						value: statement[operator],
					};
					output = [...output, ob];
				}
			}
		});
	}
	return output;
};

/**
 * Values are reformmated into the form service json format
 *
 * @param values filter array
 * @returns form service json format
 */
export const reformatFilter = (values: FilterValue[]) => {
	return values.map(
		({ filter, value, field, parentFilter, operator }: FilterValue) => {
			// Handles filters with OR differently so that they can be
			// grouped together
			if (filter === filterValues.OR) {
				const nestedValues = value.map(({ value }: FilterValue) => {
					return {
						[field as string]: {
							[operatorValues.EXACT]: {
								value: !TextValidation(value.value)
									? ""
									: parseInt(value.value)
									? parseInt(value.value)
									: value.value.trim(),
							},
						},
					};
				});
				// Groups each value under the same OR filter
				return {
					[parentFilter as string]: [{ [filter]: [...nestedValues] }],
				};
			} else {
				const cleansedVals = Object.entries(value).map(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(key: [string, any]) => {
						// String values that are boolean/number values will
						// be converted into boolean/number types
						if (!TextValidation(key[1])) return {};
						if (key[1] === "true") {
							return {
								[key[0]]: true,
							};
						} else if (key[1] === "false") {
							return {
								[key[0]]: false,
							};
						}
						return {
							[operator !== operatorValues.CONTAINS &&
							operator !== operatorValues.EXACT &&
							operator !== operatorValues.EXISTS &&
							operator !== operatorValues.ONE_OF &&
							key[0] === "value"
								? operator
								: key[0]]: parseInt(key[1])
								? parseInt(key[1])
								: typeof key[1] === "boolean"
								? key[1]
								: key[1].trim(),
						};
					}
				);

				const valueObj = Object.assign({}, ...cleansedVals);

				// gt, gte, lt, lte are dealt differently because they
				// belong to range
				return {
					[filter]: [
						{
							[field !== undefined ? field.trim() : ""]: {
								[operator !== operatorValues.CONTAINS &&
								operator !== operatorValues.EXACT &&
								operator !== operatorValues.EXISTS &&
								operator !== operatorValues.ONE_OF
									? "range"
									: operator]: { ...valueObj },
							},
						},
					],
				};
			}
		}
	);
};
